import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import { FiPlus, FiTrash2, FiX, FiSend, FiGlobe, FiBriefcase } from 'react-icons/fi';
import { C } from 'shared/darkColors';

const EMPTY_FORM = { titulo: '', mensaje: '', empresa_id: '', expira_en: '' };

export default function Avisos() {
  const [avisos, setAvisos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const fetchAvisos = useCallback(async () => {
    try {
      const r = await API.get('/super/avisos');
      if (r.data.status === 'success') setAvisos(r.data.data);
    } catch (e) {
      console.error('Error cargando avisos:', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEmpresas = useCallback(async () => {
    try {
      const r = await API.get('/super/empresas');
      if (r.data.status === 'success') setEmpresas(r.data.data);
    } catch (e) {
      console.error('Error cargando empresas:', e?.message || e);
    }
  }, []);

  useEffect(() => {
    fetchAvisos();
    fetchEmpresas();
  }, [fetchAvisos, fetchEmpresas]);

  const openModal = () => {
    setForm(EMPTY_FORM);
    setModal(true);
  };

  const save = async () => {
    if (!form.titulo.trim()) return showToast('Titulo requerido', false);
    if (!form.mensaje.trim()) return showToast('Mensaje requerido', false);
    setSaving(true);
    try {
      await API.post('/super/avisos', {
        titulo: form.titulo.trim(),
        mensaje: form.mensaje.trim(),
        empresa_id: form.empresa_id ? parseInt(form.empresa_id, 10) : null,
        expira_en: form.expira_en ? new Date(form.expira_en).toISOString() : null,
      });
      showToast('Aviso enviado');
      setModal(false);
      setForm(EMPTY_FORM);
      fetchAvisos();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al enviar';
      showToast(msg, false);
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('Desactivar este aviso?')) return;
    try {
      await API.delete(`/super/avisos/${id}`);
      showToast('Aviso desactivado');
      fetchAvisos();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al desactivar';
      showToast(msg, false);
    }
  };

  if (loading) return <div style={C.loading}>Cargando...</div>;

  return (
    <div className="sa-av-wrapper" style={C.wrapper}>
      <style>{`@keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 768px) {
          .sa-av-wrapper { padding: 16px 12px !important; }
          .sa-av-section { padding: 18px !important; border-radius: 12px !important; }
          .sa-av-section-head { flex-direction: column; gap: 12px; align-items: flex-start; }
          .sa-av-modal-box { max-width: 95vw !important; padding: 24px !important; max-height: 90vh !important; border-radius: 16px !important; }
        }
      `}</style>
      <div style={C.topBar}>
        <div>
          <div style={C.title}>Avisos y anuncios</div>
          <div style={C.subtitle}>Envía mensajes a los trabajadores (global o por empresa)</div>
        </div>
      </div>

      <div className="sa-av-section" style={C.section}>
        <div className="sa-av-section-head" style={C.sectionHead}>
          <div style={C.sectionTitle}>Avisos ({avisos.length})</div>
          <button onClick={openModal} style={C.btnPrimary}>
            <FiPlus size={14} /> Nuevo aviso
          </button>
        </div>

        {avisos.length === 0 ? (
          <div style={C.emptyRow}>No hay avisos creados</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {avisos.map((a) => (
              <div
                key={a.id}
                style={{
                  background: 'var(--agx-card, #fff)',
                  border: '1px solid var(--agx-border, #e5e7eb)',
                  borderRadius: '12px',
                  padding: '14px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: 'var(--agx-text, #111827)' }}>
                      {a.titulo}
                    </span>
                    {a.global ? (
                      <span style={{ ...C.badge, ...C.badgeGreen }}>
                        <FiGlobe size={11} style={{ marginRight: '3px' }} /> Global
                      </span>
                    ) : (
                      <span style={{ ...C.badge, background: '#eff6ff', color: '#2563eb' }}>
                        <FiBriefcase size={11} style={{ marginRight: '3px' }} /> {a.empresa_nombre || a.empresa_id}
                      </span>
                    )}
                    {a.activo === false && <span style={{ ...C.badge, ...C.badgeRed }}>Inactivo</span>}
                    {a.expira_en && (
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                        expira {new Date(a.expira_en).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: '6px', color: 'var(--agx-muted, #6b7280)', fontSize: '13px' }}>
                    {a.mensaje}
                  </div>
                </div>
                {a.activo !== false && (
                  <button onClick={() => eliminar(a.id)} style={C.btnDanger}>
                    <FiTrash2 size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <div style={C.modal} onClick={() => setModal(false)}>
          <div className="sa-av-modal-box" style={C.modalBox} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
              }}
            >
              <div style={C.modalTitle}>Nuevo aviso</div>
              <button
                onClick={() => setModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  padding: '4px',
                  borderRadius: '8px',
                }}
              >
                <FiX size={20} />
              </button>
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Titulo</label>
              <input
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                style={C.formInput}
                placeholder="Ej: Mantenimiento programado"
              />
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Mensaje</label>
              <textarea
                value={form.mensaje}
                onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                style={{ ...C.formInput, minHeight: '90px', resize: 'vertical' }}
                placeholder="Contenido del aviso..."
              />
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Destinatario</label>
              <select
                value={form.empresa_id}
                onChange={(e) => setForm({ ...form, empresa_id: e.target.value })}
                style={C.formInput}
              >
                <option value="">Global (todas las empresas)</option>
                {empresas.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Expiración (opcional)</label>
              <input
                type="datetime-local"
                value={form.expira_en}
                onChange={(e) => setForm({ ...form, expira_en: e.target.value })}
                style={C.formInput}
              />
            </div>
            <div style={C.formActions}>
              <button onClick={() => setModal(false)} style={C.btnOutline}>
                Cancelar
              </button>
              <button
                onClick={save}
                disabled={saving}
                style={{ ...C.btnPrimary, opacity: saving ? 0.6 : 1 }}
              >
                <FiSend size={14} /> Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ ...C.toast, background: toast.ok ? '#111827' : '#dc2626' }}>{toast.msg}</div>
      )}
    </div>
  );
}
